import * as React from "react";
import * as ReactDOM from "react-dom";
import { cn } from "@/lib/utils";

type DialogContextValue = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
};

const DialogContext = React.createContext<DialogContextValue | null>(null);

function useDialogContext() {
	const ctx = React.useContext(DialogContext);
	if (!ctx) throw new Error("Dialog components must be used within <Dialog>");
	return ctx;
}

type DialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	children: React.ReactNode;
};

export function Dialog({ open, onOpenChange, children }: DialogProps) {
	return <DialogContext.Provider value={{ open, onOpenChange }}>{children}</DialogContext.Provider>;
}

export function DialogContent({
	className,
	children,
}: React.HTMLAttributes<HTMLDivElement> & { children: React.ReactNode }) {
	const { open, onOpenChange } = useDialogContext();
	const contentRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		if (!open) return;
		const node = contentRef.current;
		if (!node) return;
		const focusables = Array.from(
			node.querySelectorAll<HTMLElement>(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
			),
		).filter((el) => !el.hasAttribute("disabled"));
		const firstFocusable = focusables[0];
		firstFocusable?.focus();

		function onKeyDown(event: KeyboardEvent) {
			if (event.key === "Escape") {
				event.preventDefault();
				onOpenChange(false);
				return;
			}
			if (event.key !== "Tab") return;
			if (focusables.length === 0) return;
			const first = focusables[0];
			const last = focusables[focusables.length - 1];
			const active = document.activeElement as HTMLElement | null;
			if (event.shiftKey && active === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && active === last) {
				event.preventDefault();
				first.focus();
			}
		}

		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [open, onOpenChange]);

	if (!open || typeof document === "undefined") return null;

	return ReactDOM.createPortal(
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
			<button
				type="button"
				className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px]"
				onClick={() => onOpenChange(false)}
				aria-label="Close dialog"
			/>
			<div
				role="dialog"
				aria-modal="true"
				ref={contentRef}
				className={cn(
					"relative w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl",
					className,
				)}
			>
				{children}
			</div>
		</div>,
		document.body,
	);
}

export function DialogHeader({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) {
	return <div className={cn("space-y-2", className)} {...props} />;
}

export function DialogTitle({
	className,
	...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
	return <h2 className={cn("text-lg font-semibold text-slate-900", className)} {...props} />;
}

export function DialogDescription({
	className,
	...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
	return <p className={cn("text-sm text-slate-600", className)} {...props} />;
}

export function DialogFooter({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) {
	return <div className={cn("mt-5 flex justify-end gap-2", className)} {...props} />;
}
